import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { CalendarSlot } from '../../types/calendar.types';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './ImprovedBookingModal.css';
import { API_BASE_URL, BASE_URL } from '../../config/api.config';
import {
  calculateOptimalDistribution,
  RoomType
} from '../../utils/accommodationAlgorithm';
import { tourTypeCodesMatch } from '../../utils/pricingUtils';
import { CUSTOMER_TYPE_OPTIONS, hasOrganizationName, normalizeCustomerType, organizationNameLabel } from '../../utils/customerType';
import VoucherCodeInput, { ValidatedVoucher } from '../booking/VoucherCodeInput';
import { imOnlineAngebot } from '../../utils/zubuchung';

interface ImprovedBookingModalProps {
  slot: CalendarSlot;
  availableSlotsForDay: CalendarSlot[]; // All slots for the selected day
  onClose: () => void;
  onSuccess: (bookingId: number, voucherCode?: string, voucherDiscount?: number) => void;
}

interface CalculatedAddon {
  addOnServiceId: number | null;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  quantityFree: number;
  quantityPaid: number;
  pricePerUnit: number;
  grossPricePerUnit?: number;
  taxRate?: number;
  subtotal: number;
  isManuallyAdjusted: boolean;
  isOptionalChoice?: boolean;
  isIncludedInTourPrice?: boolean;
  quantityMultiplier?: number; // Multiplier for optional inclusive addons (e.g., 2 = 2x per person)
  originalIncludedQuantity?: number;
  // Accommodation fields
  isAccommodation?: boolean;
  bedsPerUnit?: number;
  // T-058 Stufe 2 (v1.8.61): optional zubuchbar (Menge 0 vorbelegt, berechnet nur bei Auswahl)
  isOptionalExtra?: boolean;
}

// T-060 (18.09.2026, Kundin-Befund 17.09.): Terminfreigabe (Anlage v1.4 Abschnitt 2.1 Punkt 12).
// Hat ein Termin die Mindestteilnehmerzahl erreicht (Buchungsstand >= minCapacity), ist er
// freigegeben und auch fuer Kleingruppen und Einzelpersonen buchbar. Das Backend laesst das zu
// (AvailabilityService: totalAfterBooking >= 30), nur dieser Dialog verlangte bisher 30 je Buchung.
// Unterhalb der Schwelle bleibt das Minimum je Buchung = minCapacity (nicht freigegebene Termine
// erscheinen hier ohnehin nicht, Befund B7).
const getEffectiveMinParticipants = (s: CalendarSlot): number =>
  (s.currentBookings ?? 0) >= s.minCapacity ? 1 : s.minCapacity;

// Startwert: bei freigegebenen Terminen hoechstens die freien Plaetze, sonst wie bisher 30
const getInitialParticipants = (s: CalendarSlot): number => {
  const min = getEffectiveMinParticipants(s);
  const start = Math.max(min, min === 1 ? Math.min(30, s.availableCapacity) : 30);
  return start;
};

const ImprovedBookingModal: React.FC<ImprovedBookingModalProps> = ({
  slot,
  availableSlotsForDay,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot>(slot);
  const [participants, setParticipants] = useState(() => getInitialParticipants(slot));
  // T-060: wirksames Minimum je Buchung (1 auf freigegebenen Terminen, sonst minCapacity)
  const effectiveMinParticipants = getEffectiveMinParticipants(selectedSlot);
  const [addons, setAddons] = useState<CalculatedAddon[]>([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [availableAddons, setAvailableAddons] = useState<any[]>([]);
  const [loadingAvailableAddons, setLoadingAvailableAddons] = useState(false);
  const [showAddonSelector, setShowAddonSelector] = useState(false);
  const [configuredAddonIds, setConfiguredAddonIds] = useState<Set<number>>(new Set());

  // Voucher state
  const [appliedVoucher, setAppliedVoucher] = useState<ValidatedVoucher | null>(null);
  const [voucherExpanded, setVoucherExpanded] = useState(false);

  // Tiered pricing state
  const [currentPricePerPerson, setCurrentPricePerPerson] = useState<number>(slot.pricePerPerson);
  const [tierDescription, setTierDescription] = useState<string>('Basispreis');
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [tourTypeId, setTourTypeId] = useState<number | null>(null);
  // FixedPrice (Festpreis) support
  const [pricingMode, setPricingMode] = useState<'PerPerson' | 'FixedPrice'>('PerPerson');
  const [fixedPrice, setFixedPrice] = useState<number | null>(null);
  // Anzahlungssatz je Tour-Typ (Anlage v1.3 Abschnitt 2.5 Punkt 2), Standard 20 Prozent
  const [depositRate, setDepositRate] = useState<number>(0.2);
  // Komplettzahlungs-Wahl Endkunde (Anlage v1.3 Abschnitt 2.5 Punkt 3)
  const [paymentChoice, setPaymentChoice] = useState<'deposit' | 'full'>('deposit');

  // Pflichtfelder im Buchungsablauf (Anlage v1.4 Abschnitt 2.8 Punkte 2 und 3):
  // Anrede, Vorname, Nachname, Straße, Hausnummer, PLZ, Ort, Handynummer, E-Mail mit Doppeleingabe
  const [contact, setContact] = useState({
    salutation: '',
    firstName: '',
    lastName: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    phone: '',
    landlinePhone: '', // T-100 (v1.8.24): Festnetz freiwillig
    email: '',
    emailRepeat: '',
    customerType: 'private', // T-099 (v1.8.24): private, business, club, publicauthority
    companyName: '', // T-099: Name der Firma, des Vereins oder der Behörde
  });

  const updateContact = (field: keyof typeof contact, value: string) => {
    setContact(prev => ({ ...prev, [field]: value }));
  };

  // T-073 (v1.8.16, Anlage 2.1 Punkt 11): freiwillige Notiz an Frankenfloß, Payload specialRequests,
  // Anzeige im Admin unter "Besondere Wünsche" (Buchungsdetails) und in der Bestellliste (Notiz)
  const SPECIAL_REQUESTS_MAX = 1000;
  const [specialRequests, setSpecialRequests] = useState('');

  // Pflichtfeld-Validierung (2.8.2), Handynummer-Format gemäß 2.8.3
  const validateContact = (): string[] => {
    const errors: string[] = [];
    if (!contact.salutation) errors.push('Bitte Anrede auswählen.');
    if (!contact.firstName.trim()) errors.push('Bitte Vornamen angeben.');
    if (!contact.lastName.trim()) errors.push('Bitte Nachnamen angeben.');
    if (!contact.street.trim()) errors.push('Bitte Straße angeben.');
    if (!contact.houseNumber.trim()) errors.push('Bitte Hausnummer angeben.');
    if (!/^[0-9]{4,5}$/.test(contact.postalCode.trim())) errors.push('Bitte gültige Postleitzahl angeben.');
    if (!contact.city.trim()) errors.push('Bitte Ort angeben.');
    const phoneTrimmed = contact.phone.trim();
    const phoneDigits = phoneTrimmed.replace(/[^0-9]/g, '').length;
    if (!phoneTrimmed) {
      errors.push('Bitte Handynummer angeben.');
    } else if (phoneDigits < 7 || phoneDigits > 20 || !/^\+?[0-9 /\-()]+$/.test(phoneTrimmed)) {
      errors.push('Ungültige Handynummer (Ziffern, optional führendes +, 7 bis 20 Ziffern).');
    }
    // T-100 (v1.8.24): Festnetz freiwillig, Formatprüfung nur wenn angegeben
    const landlineTrimmed = contact.landlinePhone.trim();
    if (landlineTrimmed) {
      const landlineDigits = landlineTrimmed.replace(/[^0-9]/g, '').length;
      if (landlineDigits < 7 || landlineDigits > 20 || !/^\+?[0-9 /\-()]+$/.test(landlineTrimmed)) {
        errors.push('Ungültige Festnetznummer (Ziffern, optional führendes +, 7 bis 20 Ziffern) oder Feld leer lassen.');
      }
    }
    // T-099 (v1.8.24): bei Geschäftskunde, Verein, Verwaltung ist der Name der Organisation Pflicht
    if (hasOrganizationName(contact.customerType) && !contact.companyName.trim()) {
      errors.push(`Bitte ${organizationNameLabel(contact.customerType)} angeben.`);
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email.trim())) {
      errors.push('Bitte gültige E-Mail-Adresse angeben.');
    } else if (contact.email.trim().toLowerCase() !== contact.emailRepeat.trim().toLowerCase()) {
      errors.push('Die E-Mail-Adressen stimmen nicht überein, bitte beide Felder prüfen.');
    }
    return errors;
  };

  // Kontaktdaten mit Werten aus dem Benutzerkonto vorbelegen
  // (E-Mail-Wiederholung bewusst leer, Doppeleingabe gemäß 2.8.3)
  useEffect(() => {
    if (user) {
      setContact(prev => ({
        ...prev,
        firstName: prev.firstName || user.firstName || '',
        lastName: prev.lastName || user.lastName || '',
        email: prev.email || user.email || '',
        phone: prev.phone || (user as any).phoneNumber || (user as any).phone || '',
        // T-100 / T-099 (v1.8.24): Festnetz, Kundentyp und Name der Organisation aus dem Kundenkonto
        landlinePhone: prev.landlinePhone || (user as any).landlinePhone || '',
        customerType: normalizeCustomerType((user as any).customerType),
        companyName: prev.companyName || (user as any).companyName || '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Error state for validation errors
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Format date for display
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time for display (converts "11:00:00" to "11:00")
  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    // Handle both "HH:mm:ss" and "HH:mm" formats
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  };

  // Format currency for German locale (1234.56 -> "1.234,56 €")
  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' €';
  };

  const getTourTypeName = (tourType: string): string => {
    const names: { [key: string]: string } = {
      'STANDARD': 'Standard Floßfahrt',
      'ALL_IN': 'All-In Floßfahrt',
      'ALL_IN_PREMIUM': 'All-In Premium',
      'EXCLUSIVE': 'Exklusiv Tour',
      'WEDDING': 'Hochzeits-Tour'
    };
    return names[tourType] || tourType;
  };

  // Load addons when variant or participant count changes
  useEffect(() => {
    loadAddons();
  }, [selectedSlot.tourType, participants]);

  // Load all available addons when tour type changes
  useEffect(() => {
    loadAvailableAddons();
  }, [selectedSlot.tourType]);

  // Load tourTypeId on mount
  useEffect(() => {
    const fetchTourTypeId = async () => {
      try {
        console.log('ImprovedBookingModal: Fetching tourTypeId for tourType:', selectedSlot.tourType);

        const response = await fetch(`${API_BASE_URL}/tour-types`);
        if (!response.ok) {
          console.error('Failed to fetch tour types');
          return;
        }

        const tourTypes = await response.json();
        console.log('Loaded tour types:', tourTypes);
        console.log('Tour types with pricing info:', tourTypes.map((tt: any) =>
          `${tt.code}/${tt.name}: pricingMode=${tt.pricingMode}, fixedPrice=${tt.fixedPrice}`
        ));

        // Find matching tour type with flexible code matching (WEDDING <-> HOCHZEIT etc.)
        const matchingTourType = tourTypes.find((tt: any) => {
          // Use the tourTypeCodesMatch function for flexible matching
          const codeMatch = tourTypeCodesMatch(tt.code, selectedSlot.tourType);
          const nameMatch = tourTypeCodesMatch(tt.name, selectedSlot.tourType);
          return codeMatch || nameMatch;
        });

        if (matchingTourType) {
          console.log('Found matching tour type:', matchingTourType);
          console.log('Matching tour type pricingMode:', matchingTourType.pricingMode);
          console.log('Matching tour type fixedPrice:', matchingTourType.fixedPrice);
          setTourTypeId(matchingTourType.id);

          // Anzahlungssatz des Tour-Typs uebernehmen (z.B. Exklusiv/Hochzeit 40 Prozent)
          if (typeof matchingTourType.depositPercentage === 'number' &&
              matchingTourType.depositPercentage > 0 && matchingTourType.depositPercentage < 1) {
            setDepositRate(matchingTourType.depositPercentage);
          }

          // If this is a FixedPrice tour type, set it immediately
          if (matchingTourType.pricingMode === 'FixedPrice' && matchingTourType.fixedPrice > 0) {
            console.log('FESTPREIS detected! Setting pricingMode=FixedPrice, fixedPrice=', matchingTourType.fixedPrice);
            setPricingMode('FixedPrice');
            setFixedPrice(matchingTourType.fixedPrice);
            setCurrentPricePerPerson(matchingTourType.fixedPrice);
            setTierDescription('Festpreis');
          }
        } else {
          console.warn('No matching tour type found for:', selectedSlot.tourType);
          console.warn('Available tour types:', tourTypes.map((tt: any) => tt.code));
        }
      } catch (error) {
        console.error('Error fetching tour type ID:', error);
      }
    };

    fetchTourTypeId();
  }, [selectedSlot.tourType]);

  // Calculate tiered price when participants or tourTypeId changes
  useEffect(() => {
    if (tourTypeId && participants > 0) {
      calculateTieredPrice(participants);
    }
  }, [participants, tourTypeId]);

  const calculateTieredPrice = async (participantCount: number) => {
    if (!tourTypeId) {
      console.log('ImprovedBookingModal: No tourTypeId available, using base price');
      return;
    }

    setLoadingPrice(true);
    try {
      console.log(`ImprovedBookingModal: Calculating tiered price for tourTypeId=${tourTypeId}, participants=${participantCount}`);

      const response = await fetch(
        `${API_BASE_URL}/tour-types/${tourTypeId}/calculate-price?participants=${participantCount}`
      );

      if (!response.ok) {
        console.error('Failed to calculate tiered price:', response.status);
        setLoadingPrice(false);
        return;
      }

      const data = await response.json();
      console.log('Tiered price calculation result:', data);

      setCurrentPricePerPerson(data.pricePerPerson);
      setTierDescription(data.tierDescription);
      // Set Festpreis (FixedPrice) mode if applicable
      setPricingMode(data.pricingMode === 'FixedPrice' ? 'FixedPrice' : 'PerPerson');
      setFixedPrice(data.fixedPrice || null);
    } catch (error) {
      console.error('Error calculating tiered price:', error);
    } finally {
      setLoadingPrice(false);
    }
  };

  const loadAddons = async () => {
    setLoadingAddons(true);
    try {
      console.log('Loading addons for tourType:', selectedSlot.tourType, 'participants:', participants);

      const response = await fetch(
        `${API_BASE_URL}/tour-types/${selectedSlot.tourType}/addons/calculate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            participantCount: participants
          })
        }
      );

      console.log('Addon API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Loaded addons:', data);

        // Preselect first optional choice addon with participant count * multiplier
        let isFirstOptionalChoice = true;
        let addonsWithPreselection = (data || []).map((addon: CalculatedAddon) => {
          if (addon.isOptionalChoice) {
            // Get the multiplier (default to 1 if not set)
            const multiplier = addon.quantityMultiplier || 1;
            const expectedTotal = participants * multiplier;

            if (isFirstOptionalChoice) {
              // First optional choice gets participant count * multiplier as preselection
              isFirstOptionalChoice = false;
              const originalIncluded = addon.originalIncludedQuantity ?? addon.quantityFree ?? 0;
              // When IsIncludedInTourPrice, all are free
              const quantityFree = addon.isIncludedInTourPrice ? expectedTotal : Math.min(expectedTotal, originalIncluded);
              const quantityPaid = addon.isIncludedInTourPrice ? 0 : Math.max(0, expectedTotal - originalIncluded);
              return {
                ...addon,
                quantityTotal: expectedTotal,
                quantityFree,
                quantityPaid,
                subtotal: quantityPaid * addon.pricePerUnit,
                originalIncludedQuantity: originalIncluded
              };
            } else {
              // All other optional choices start at 0
              return {
                ...addon,
                quantityTotal: 0,
                quantityFree: 0,
                quantityPaid: 0,
                subtotal: 0,
                originalIncludedQuantity: addon.originalIncludedQuantity ?? addon.quantityFree ?? 0
              };
            }
          }
          // Non-optional-choice addons: keep original values but store originalIncludedQuantity
          return {
            ...addon,
            originalIncludedQuantity: addon.originalIncludedQuantity ?? addon.quantityFree ?? 0
          };
        });

        // Apply smart distribution for accommodation addons
        const accommodationAddonsList = addonsWithPreselection.filter(
          (a: CalculatedAddon) => a.isAccommodation && a.bedsPerUnit && a.bedsPerUnit > 0
        );

        if (accommodationAddonsList.length > 0) {
          console.log('Found accommodation addons, applying smart distribution for', participants, 'guests');
          console.log('Accommodation addons isIncludedInTourPrice:', accommodationAddonsList.map((a: CalculatedAddon) =>
            `${a.addOnServiceName}: isIncludedInTourPrice=${a.isIncludedInTourPrice}`
          ));

          // Convert addons to RoomType format for the algorithm
          const roomTypes: RoomType[] = accommodationAddonsList.map((addon: CalculatedAddon) => ({
            id: addon.addOnServiceId || 0,
            bedsPerUnit: addon.bedsPerUnit || 1,
            price: addon.pricePerUnit,
            grossPrice: addon.grossPricePerUnit || addon.pricePerUnit * (1 + (addon.taxRate || 0.19)),
            name: addon.addOnServiceName
          }));

          // Calculate optimal distribution
          const distribution = calculateOptimalDistribution(participants, roomTypes);
          console.log('Optimal distribution:', Object.fromEntries(distribution));

          // Apply distribution to accommodation addons
          // IMPORTANT: Respect isIncludedInTourPrice - if false, ALL rooms are paid (extra)
          addonsWithPreselection = addonsWithPreselection.map((addon: CalculatedAddon) => {
            if (addon.isAccommodation && addon.bedsPerUnit && addon.bedsPerUnit > 0 && addon.addOnServiceId) {
              const newQuantity = distribution.get(addon.addOnServiceId) || 0;

              // FIX: When isIncludedInTourPrice is false, ALL rooms are paid (extra)
              // When isIncludedInTourPrice is true, rooms up to included quantity are free
              let quantityFree: number;
              let quantityPaid: number;

              if (addon.isIncludedInTourPrice) {
                // Included in tour price - all rooms are free
                quantityFree = newQuantity;
                quantityPaid = 0;
              } else {
                // NOT included in tour price - ALL rooms are paid (extra)
                quantityFree = 0;
                quantityPaid = newQuantity;
              }

              console.log(`[loadAddons] ${addon.addOnServiceName}: isIncludedInTourPrice=${addon.isIncludedInTourPrice}, qty=${newQuantity}, free=${quantityFree}, paid=${quantityPaid}`);

              return {
                ...addon,
                quantityTotal: newQuantity,
                quantityFree,
                quantityPaid,
                subtotal: quantityPaid * addon.pricePerUnit
              };
            }
            return addon;
          });
        }

        // T-058 Stufe 2 (v1.8.61): gewählte Mengen der optional zubuchbaren Speisen bleiben bei geänderter Teilnehmerzahl erhalten
        // (bisher wurde die Liste neu geladen und eine Auswahl ging verloren)
        setAddons(prev => addonsWithPreselection.map((addon: CalculatedAddon) => {
          if (!addon.isOptionalExtra) return addon;
          const vorher = prev.find(p => p.addOnServiceId === addon.addOnServiceId);
          if (!vorher || !(vorher.quantityTotal > 0)) return addon;
          const menge = vorher.quantityTotal;
          return { ...addon, quantityTotal: menge, quantityFree: 0, quantityPaid: menge, subtotal: menge * addon.pricePerUnit, isManuallyAdjusted: true };
        }));
      } else {
        const errorText = await response.text();
        console.error('Addon API error:', response.status, errorText);
        setAddons([]);
      }
    } catch (error) {
      console.error('Error loading addons:', error);
      setAddons([]);
    } finally {
      setLoadingAddons(false);
    }
  };

  const loadAvailableAddons = async () => {
    setLoadingAvailableAddons(true);
    try {
      // First, get all categories
      const categoriesResponse = await fetch(`${API_BASE_URL}/AddOn/categories?includeInactive=false`);

      if (!categoriesResponse.ok) {
        console.error('Failed to load addon categories');
        setAvailableAddons([]);
        return;
      }

      const categories = await categoriesResponse.json();
      console.log('Loaded addon categories:', categories);

      // Then, fetch all services for each category
      const allServices: any[] = [];

      for (const category of categories) {
        const servicesResponse = await fetch(
          `${API_BASE_URL}/AddOn/categories/${category.id}/services?includeInactive=false`
        );

        if (servicesResponse.ok) {
          const services = await servicesResponse.json();
          allServices.push(...services);
        }
      }

      console.log('Loaded all available addons:', allServices);
      setAvailableAddons(allServices);

      // Lade konfigurierte Addons für den Tour-Typ (für Filterung)
      if (selectedSlot.tourType) {
        try {
          const configResponse = await fetch(`${API_BASE_URL}/tour-types/${selectedSlot.tourType}/addons`);
          if (configResponse.ok) {
            const configData = await configResponse.json();
            // T-058 Stufe 2 (v1.8.61): nur Konfigurationen im Online-Angebot (Automatisch oder Optional zubuchbar);
            // "Nur Verwaltung" bleibt für Gäste unsichtbar
            const configuredIds = new Set<number>(configData.filter((c: any) => imOnlineAngebot(c)).map((c: any) => c.addOnServiceId));
            setConfiguredAddonIds(configuredIds);
            console.log(`Konfigurierte Addons für ${selectedSlot.tourType}:`, configuredIds);
          }
        } catch (configErr) {
          console.warn('Konnte Tour-Type Konfiguration nicht laden:', configErr);
          setConfiguredAddonIds(new Set());
        }
      }
    } catch (error) {
      console.error('Error loading available addons:', error);
      setAvailableAddons([]);
    } finally {
      setLoadingAvailableAddons(false);
    }
  };

  const handleAddManualAddon = (addonId: number) => {
    const selectedAddon = availableAddons.find(a => a.id === addonId);
    if (!selectedAddon) return;

    // Check if addon is already in the list
    const existingIndex = addons.findIndex(a => a.addOnServiceId === addonId);
    if (existingIndex >= 0) {
      // Already exists, just increase quantity
      if (selectedAddon.isAccommodation) {
        handleAccommodationQuantityChange(addonId, 1);
      } else {
        handleAddonQuantityChange(existingIndex, addons[existingIndex].quantityTotal + 1);
      }
    } else {
      // Add new addon with quantity 0 (user will adjust)
      // WICHTIG: isAccommodation und bedsPerUnit aus dem Service übernehmen!
      const newAddon: CalculatedAddon = {
        addOnServiceId: selectedAddon.id,
        addOnServiceName: selectedAddon.name,
        pricingType: selectedAddon.pricingType,
        quantityTotal: 0,
        quantityFree: 0,
        quantityPaid: 0,
        pricePerUnit: selectedAddon.price,
        grossPricePerUnit: selectedAddon.grossPrice,
        taxRate: selectedAddon.taxRate,
        subtotal: 0,
        isManuallyAdjusted: true,
        // Accommodation-Eigenschaften aus dem AddOnService übernehmen
        isAccommodation: selectedAddon.isAccommodation || false,
        bedsPerUnit: selectedAddon.bedsPerUnit || undefined
      };

      // Bei Accommodation: Nach dem Hinzufügen die Kostenberechnung aktualisieren
      if (newAddon.isAccommodation && newAddon.bedsPerUnit) {
        setAddons(prev => recalculateAccommodationCosts([...prev, newAddon]));
      } else {
        setAddons(prev => [...prev, newAddon]);
      }
    }

    setShowAddonSelector(false);
  };

  const getUnselectedAddons = () => {
    return availableAddons.filter(available => {
      // Nur Addons anzeigen, die für diesen Tour-Typ im Online-Angebot konfiguriert sind
      // T-058 Stufe 2 (v1.8.61): ohne Konfiguration nichts (bisher alle aktiven Zusatzoptionen, auch "Nur Verwaltung")
      const isConfigured = configuredAddonIds.has(available.id);
      // Und die noch nicht ausgewählt wurden
      const notAlreadySelected = !addons.some(addon => addon.addOnServiceId === available.id);
      return isConfigured && notAlreadySelected;
    });
  };

  // Handle variant selection
  const handleVariantChange = (slotId: number) => {
    const newSlot = availableSlotsForDay.find(s => s.id === slotId);
    if (newSlot) {
      setSelectedSlot(newSlot);
      // Reset participants to min capacity of new slot (T-060: freigegebene Termine ab 1 Person)
      const newParticipantCount = getInitialParticipants(newSlot);
      setParticipants(newParticipantCount);
      // Reset price to new slot's base price initially
      setCurrentPricePerPerson(newSlot.pricePerPerson);
      setTierDescription('Basispreis');
      // Clear any validation errors when changing variant
      setValidationErrors([]);
      // Note: useEffect will trigger and recalculate the tiered price automatically
    }
  };

  // Handle addon quantity change
  const handleAddonQuantityChange = (index: number, newQuantity: number) => {
    setAddons(prev => {
      const updated = [...prev];
      const addon = { ...updated[index] };

      addon.quantityTotal = Math.max(0, newQuantity);
      addon.quantityPaid = Math.max(0, addon.quantityTotal - addon.quantityFree);
      addon.subtotal = addon.quantityPaid * addon.pricePerUnit;
      addon.isManuallyAdjusted = true;

      updated[index] = addon;
      return updated;
    });
  };

  const calculateSubtotal = () => {
    // FESTPREIS: Use fixedPrice directly (not multiplied by participants)
    // PERPERSON: Use currentPricePerPerson * participants
    const basePrice = pricingMode === 'FixedPrice' && fixedPrice
      ? fixedPrice
      : currentPricePerPerson * participants;
    // WICHTIG: Addon-Preise müssen als BRUTTO berechnet werden
    // pricePerUnit ist NETTO, daher mit individuellem Steuersatz umrechnen
    const addonsTotal = addons.reduce((sum, addon) => {
      const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
      return sum + addon.quantityPaid * grossPrice;
    }, 0);
    return basePrice + addonsTotal;
  };

  const calculateTotalPrice = () => {
    const subtotal = calculateSubtotal();
    const voucherDiscount = appliedVoucher?.amountToApply || 0;
    return Math.max(0, subtotal - voucherDiscount);
  };

  const calculatePriceBreakdown = () => {
    const subtotal = calculateSubtotal();
    const voucherDiscount = appliedVoucher?.amountToApply || 0;
    const brutto = Math.max(0, subtotal - voucherDiscount);
    const netto = brutto / 1.19; // 19% MwSt. in Deutschland
    const mwst = brutto - netto;
    return { brutto, netto, mwst, subtotal, voucherDiscount };
  };

  // === Addon-Bereiche aufteilen ===

  // Bereich 1: Auswahl pro Teilnehmer (isOptionalChoice === true)
  const optionalChoiceAddons = addons.filter(a => a.isOptionalChoice);

  // Nicht-optionale Addons (excluding accommodation)
  const regularAddons = addons.filter(a => !a.isOptionalChoice && !a.isAccommodation);

  // Bereich 2: Inklusivleistungen (quantityPaid === 0 und quantityTotal > 0)
  const inclusiveAddons = regularAddons.filter(a => a.quantityPaid === 0 && a.quantityTotal > 0);

  // Bereich 3: Extras (quantityPaid > 0 oder quantityTotal === 0)
  const extraAddons = regularAddons.filter(a => a.quantityPaid > 0 || a.quantityTotal === 0);

  // Bereich 4: Übernachtung/Zimmerbelegung (isAccommodation === true)
  const accommodationAddons = addons.filter(a => a.isAccommodation && a.bedsPerUnit && a.bedsPerUnit > 0);

  // === Accommodation helper functions ===

  // Calculate total beds from accommodation addons
  const getAccommodationTotalBeds = (): number => {
    return accommodationAddons.reduce((sum, addon) => {
      return sum + (addon.quantityTotal * (addon.bedsPerUnit || 0));
    }, 0);
  };

  // Get bed status (ok, warning, error)
  const getAccommodationBedStatus = (): 'ok' | 'warning' | 'error' => {
    if (accommodationAddons.length === 0) return 'ok';
    const totalBeds = getAccommodationTotalBeds();
    if (totalBeds === participants) return 'ok';
    if (totalBeds > participants) return 'warning';
    return 'error';
  };

  /**
   * Berechnet kostenpflichtige Zimmer basierend auf Tour-Konfiguration
   *
   * WICHTIG: Unterscheidung nach isIncludedInTourPrice:
   * - isIncludedInTourPrice = true: Zimmer sind im Tourpreis enthalten, nur Überschuss wird berechnet
   * - isIncludedInTourPrice = false: ALLE Zimmer werden zusätzlich berechnet (kostenpflichtig)
   */
  const recalculateAccommodationCosts = (addonList: CalculatedAddon[]): CalculatedAddon[] => {
    // Filter for ALL accommodation addons
    const accommodationList = addonList.filter(a => a.isAccommodation && a.bedsPerUnit);
    if (accommodationList.length === 0) return addonList;

    // Berechne totale Betten (nur für inkludierte Zimmer relevant)
    const includedAccommodations = accommodationList.filter(a => a.isIncludedInTourPrice);
    const totalIncludedBeds = includedAccommodations.reduce((sum, a) => sum + (a.quantityTotal * (a.bedsPerUnit || 1)), 0);

    console.log(`[recalculateAccommodationCosts] participants=${participants}, includedBeds=${totalIncludedBeds}`);

    // Berechne Excess-Betten nur für inkludierte Zimmer
    const excessBeds = Math.max(0, totalIncludedBeds - participants);
    console.log(`[Accommodation] Excess beds (from included): ${excessBeds}`);

    // Sortiere inkludierte Zimmer nach Betten (kleinste zuerst für Excess-Berechnung)
    const sortedIncluded = [...includedAccommodations].sort((a, b) =>
      (a.bedsPerUnit || 1) - (b.bedsPerUnit || 1)
    );

    let remainingExcess = excessBeds;
    const excessAllocation = new Map<number, number>(); // addonId -> überschüssige Zimmer

    // Verteile überschüssige Betten auf inkludierte Zimmer (kleinste zuerst)
    for (const addon of sortedIncluded) {
      if (remainingExcess <= 0) break;

      const bedsPerRoom = addon.bedsPerUnit || 1;
      const roomsInThisType = addon.quantityTotal;

      const excessRoomsForType = Math.min(
        Math.ceil(remainingExcess / bedsPerRoom),
        roomsInThisType
      );

      if (excessRoomsForType > 0 && addon.addOnServiceId) {
        excessAllocation.set(addon.addOnServiceId, excessRoomsForType);
        remainingExcess -= excessRoomsForType * bedsPerRoom;
        console.log(`[Accommodation] ${addon.addOnServiceName}: ${excessRoomsForType} Zimmer excess`);
      }
    }

    // Wende Berechnung auf alle Addons an
    return addonList.map(addon => {
      if (!addon.isAccommodation || !addon.addOnServiceId) return addon;

      // WICHTIG: Unterscheidung nach isIncludedInTourPrice!
      if (addon.isIncludedInTourPrice) {
        // Im Tourpreis enthalten: Nur Überschuss-Zimmer sind kostenpflichtig
        const excessRooms = excessAllocation.get(addon.addOnServiceId) || 0;
        const freeRooms = addon.quantityTotal - excessRooms;

        console.log(`[Accommodation] ${addon.addOnServiceName} (INCLUDED): total=${addon.quantityTotal}, free=${freeRooms}, paid=${excessRooms}`);

        return {
          ...addon,
          quantityFree: freeRooms,
          quantityPaid: excessRooms,
          subtotal: excessRooms * addon.pricePerUnit
        };
      } else {
        // NICHT im Tourpreis enthalten: ALLE Zimmer sind kostenpflichtig!
        console.log(`[Accommodation] ${addon.addOnServiceName} (NOT INCLUDED): total=${addon.quantityTotal}, ALL PAID`);

        return {
          ...addon,
          quantityFree: 0,
          quantityPaid: addon.quantityTotal,
          subtotal: addon.quantityTotal * addon.pricePerUnit
        };
      }
    });
  };

  // Handle accommodation addon quantity change
  const handleAccommodationQuantityChange = (addonId: number | null, delta: number) => {
    if (addonId === null) return;

    setAddons(prev => {
      const index = prev.findIndex(a => a.addOnServiceId === addonId);
      if (index === -1) return prev;

      const addon = prev[index];
      const newQuantity = Math.max(0, addon.quantityTotal + delta);

      // Initial update with new quantity
      let updated = [...prev];
      updated[index] = {
        ...addon,
        quantityTotal: newQuantity,
        quantityFree: newQuantity, // Will be recalculated
        quantityPaid: 0,
        subtotal: 0,
        isManuallyAdjusted: true
      };

      // Always recalculate excess bed costs for accommodation addons
      // This ensures extra costs are calculated when beds > participants
      updated = recalculateAccommodationCosts(updated);

      return updated;
    });
  };

  // Reset accommodation to optimal distribution
  const resetAccommodationToOptimal = () => {
    // Convert current accommodation addons to RoomType format
    const roomTypes: RoomType[] = accommodationAddons.map(addon => ({
      id: addon.addOnServiceId || 0,
      bedsPerUnit: addon.bedsPerUnit || 1,
      price: addon.pricePerUnit,
      grossPrice: addon.grossPricePerUnit || addon.pricePerUnit * (1 + (addon.taxRate || 0.19)),
      name: addon.addOnServiceName
    }));

    const distribution = calculateOptimalDistribution(participants, roomTypes);

    setAddons(prev => {
      // First apply optimal distribution
      let updated = prev.map(addon => {
        if (addon.isAccommodation && addon.bedsPerUnit && addon.bedsPerUnit > 0 && addon.addOnServiceId) {
          const newQuantity = distribution.get(addon.addOnServiceId) || 0;
          return {
            ...addon,
            quantityTotal: newQuantity,
            quantityFree: newQuantity,
            quantityPaid: 0,
            subtotal: 0,
            isManuallyAdjusted: false
          };
        }
        return addon;
      });

      // Then recalculate excess bed costs
      updated = recalculateAccommodationCosts(updated);
      return updated;
    });
  };

  // Calculate total selected for optional choice addons
  const getOptionalChoiceTotal = (): number => {
    return optionalChoiceAddons.reduce((sum, addon) => sum + addon.quantityTotal, 0);
  };

  // Get the quantity multiplier from optional choice addons (all should have the same multiplier)
  const getOptionalChoiceMultiplier = (): number => {
    if (optionalChoiceAddons.length === 0) return 1;
    return optionalChoiceAddons[0]?.quantityMultiplier || 1;
  };

  // Get expected total for optional choice addons (participants * multiplier)
  const getOptionalChoiceExpectedTotal = (): number => {
    return participants * getOptionalChoiceMultiplier();
  };

  // Check if optional choice validation passes (sum must equal participant count * multiplier)
  const isOptionalChoiceValid = (): boolean => {
    if (optionalChoiceAddons.length === 0) return true;
    return getOptionalChoiceTotal() === getOptionalChoiceExpectedTotal();
  };

  // Get max quantity for a specific optional choice addon
  const getMaxForOptionalAddon = (addon: CalculatedAddon): number => {
    const currentTotal = getOptionalChoiceTotal();
    const expectedTotal = getOptionalChoiceExpectedTotal();
    const remaining = expectedTotal - currentTotal;
    return addon.quantityTotal + remaining;
  };

  // Handle quantity change for optional choice addons with limit
  const handleOptionalChoiceQuantityChange = (addonId: number | null, delta: number) => {
    if (addonId === null) return;

    setAddons(prev => {
      const index = prev.findIndex(a => a.addOnServiceId === addonId);
      if (index === -1) return prev;

      const addon = prev[index];
      const newQuantity = addon.quantityTotal + delta;
      if (newQuantity < 0) return prev;

      // Check if we would exceed expected total (participants * multiplier)
      const currentTotal = optionalChoiceAddons.reduce((sum, a) => sum + a.quantityTotal, 0);
      const expectedTotal = getOptionalChoiceExpectedTotal();
      const newTotal = currentTotal - addon.quantityTotal + newQuantity;
      if (newTotal > expectedTotal) return prev;

      // Recalculate free/paid split
      const originalIncluded = addon.originalIncludedQuantity ?? 0;
      // When IsIncludedInTourPrice, all are free
      const quantityFree = addon.isIncludedInTourPrice ? newQuantity : Math.min(newQuantity, originalIncluded);
      const quantityPaid = addon.isIncludedInTourPrice ? 0 : Math.max(0, newQuantity - originalIncluded);
      const subtotal = quantityPaid * addon.pricePerUnit;

      const updated = [...prev];
      updated[index] = {
        ...addon,
        quantityTotal: newQuantity,
        quantityFree,
        quantityPaid,
        subtotal,
        isManuallyAdjusted: true
      };
      return updated;
    });
  };

  // Handle input change for optional choice addons with limit
  const handleOptionalChoiceInputChange = (addonId: number | null, value: string) => {
    if (addonId === null) return;

    const newQuantity = parseInt(value) || 0;
    if (newQuantity < 0) return;

    setAddons(prev => {
      const index = prev.findIndex(a => a.addOnServiceId === addonId);
      if (index === -1) return prev;

      const addon = prev[index];

      // Calculate max allowed for this addon (using expected total with multiplier)
      const currentTotal = optionalChoiceAddons.reduce((sum, a) => sum + a.quantityTotal, 0);
      const expectedTotal = getOptionalChoiceExpectedTotal();
      const remaining = expectedTotal - currentTotal + addon.quantityTotal;
      const clampedQuantity = Math.min(Math.max(0, newQuantity), remaining);

      // Recalculate free/paid split
      const originalIncluded = addon.originalIncludedQuantity ?? 0;
      // When IsIncludedInTourPrice, all are free
      const quantityFree = addon.isIncludedInTourPrice ? clampedQuantity : Math.min(clampedQuantity, originalIncluded);
      const quantityPaid = addon.isIncludedInTourPrice ? 0 : Math.max(0, clampedQuantity - originalIncluded);
      const subtotal = quantityPaid * addon.pricePerUnit;

      const updated = [...prev];
      updated[index] = {
        ...addon,
        quantityTotal: clampedQuantity,
        quantityFree,
        quantityPaid,
        subtotal,
        isManuallyAdjusted: true
      };
      return updated;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');

      // Validate user is logged in
      if (!user || !user.email) {
        alert('Bitte melden Sie sich an, um eine Buchung zu erstellen.');
        setLoading(false);
        return;
      }

      // Pflichtfelder pruefen (Anlage v1.4 Abschnitt 2.8 Punkte 2 und 3)
      const contactErrors = validateContact();
      if (contactErrors.length > 0) {
        setValidationErrors(contactErrors);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Validate optional choice addons (Verpflegungsauswahl)
      if (!isOptionalChoiceValid()) {
        const multiplier = getOptionalChoiceMultiplier();
        const expectedTotal = getOptionalChoiceExpectedTotal();
        const errorMsg = multiplier > 1
          ? `Bitte wählen Sie Bordspeisen für alle ${participants} Teilnehmer (${multiplier}x pro Person = ${expectedTotal}). Aktuell gewählt: ${getOptionalChoiceTotal()} von ${expectedTotal}`
          : `Bitte wählen Sie Bordspeisen für alle ${participants} Teilnehmer. Aktuell gewählt: ${getOptionalChoiceTotal()} von ${expectedTotal}`;
        setValidationErrors([errorMsg]);
        setLoading(false);
        return;
      }

      const totalPrice = calculateTotalPrice();

      // Convert time format from "HH:mm" or "HH:mm:ss" to TimeSpan format
      const timeSpan = selectedSlot.time.includes(':')
        ? selectedSlot.time.split(':').length === 2
          ? `${selectedSlot.time}:00` // Add seconds if missing
          : selectedSlot.time
        : selectedSlot.time;

      // Filter out addons with null/invalid IDs and zero quantity
      console.log('All addons before filtering:', addons);
      const validAddons = addons.filter(addon => {
        const isValid = addon.addOnServiceId !== null &&
          addon.addOnServiceId !== undefined &&
          addon.addOnServiceId > 0 &&
          addon.quantityTotal > 0;
        console.log(`Addon ${addon.addOnServiceName} (ID: ${addon.addOnServiceId}): ${isValid ? 'VALID' : 'INVALID'}`);
        return isValid;
      });
      console.log('Valid addons after filtering:', validAddons);

      const bookingPayload = {
        floatTripId: 0, // Set to 0 to trigger date/time lookup
        calendarSlotId: selectedSlot.id,
        tourType: selectedSlot.tourType, // TourType for addon configuration and quantity multiplier validation
        participantCount: participants,
        requestedDate: selectedSlot.date,
        requestedTime: timeSpan,
        variant: selectedSlot.tourType,
        // FIX: Send BRUTTO prices to backend (addon.pricePerUnit is NETTO, addon.grossPricePerUnit is BRUTTO)
        additionalServices: validAddons.map(addon => {
          const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
          const grossSubtotal = addon.quantityPaid * grossPrice;
          return {
            additionalServiceId: addon.addOnServiceId!,
            quantity: addon.quantityTotal,
            quantityFree: addon.quantityFree,
            quantityPaid: addon.quantityPaid,
            pricePerUnit: grossPrice, // BRUTTO Einzelpreis
            subtotal: grossSubtotal, // BRUTTO Zwischensumme
            isManuallyAdjusted: addon.isManuallyAdjusted
          };
        }),
        // Pflichtfelder aus dem Buchungsablauf (Anlage v1.4 Abschnitt 2.8 Punkt 2)
        customerInfo: {
          salutation: contact.salutation,
          firstName: contact.firstName.trim(),
          lastName: contact.lastName.trim(),
          email: contact.email.trim(),
          phone: contact.phone.trim(),
          landlinePhone: contact.landlinePhone.trim() || undefined, // T-100 (v1.8.24)
          customerType: contact.customerType, // T-099 (v1.8.24)
          companyName: hasOrganizationName(contact.customerType) ? contact.companyName.trim() : undefined, // T-099
          billingAddress: {
            street: `${contact.street.trim()} ${contact.houseNumber.trim()}`.trim(),
            city: contact.city.trim(),
            postalCode: contact.postalCode.trim(),
            country: 'Deutschland'
          }
        },
        // T-073: Notiz an Frankenfloß (optional), Backend übernimmt SpecialRequests in die Buchung
        specialRequests: specialRequests.trim() ? specialRequests.trim().substring(0, SPECIAL_REQUESTS_MAX) : undefined,
        // Komplettzahlungs-Wahl (Anlage v1.3 Abschnitt 2.5 Punkt 3): 'Full' = Gesamtbetrag sofort
        paymentPreference: paymentChoice === 'full' ? 'Full' : 'Advance',
        // Voucher data - wird vom Backend verarbeitet und in VoucherRedemption gespeichert
        voucherCode: appliedVoucher?.code || undefined,
        voucherRedemptionAmount: appliedVoucher?.amountToApply || undefined
      };

      console.log('Booking payload:', bookingPayload);
      console.log('Booking payload stringified:', JSON.stringify(bookingPayload, null, 2));

      const response = await fetch(`${API_BASE_URL}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingPayload)
      });

      const responseText = await response.text();
      console.log('Booking API response status:', response.status);
      console.log('Booking API response body:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch {
        console.error('Failed to parse response as JSON:', responseText);
        setValidationErrors([`Server-Fehler: ${response.status} - ${responseText.substring(0, 200)}`]);
        return;
      }

      // Check for success - Backend returns either:
      // 1. { success: true, data: { id: ... } } (wrapped response)
      // 2. { id: ..., bookingNumber: ... } (direct booking object on 201)
      const isSuccess = response.ok && (data.success || data.id);
      const bookingId = data.data?.id || data.id;

      if (isSuccess && bookingId) {
        // Clear any previous errors
        setValidationErrors([]);
        console.log('Booking successful! ID:', bookingId);
        // Pass voucher data if a voucher was applied
        onSuccess(
          bookingId,
          appliedVoucher?.code,
          appliedVoucher?.amountToApply
        );
      } else {
        // Extract detailed validation errors from response
        const errorMessages: string[] = [];

        console.log('Booking failed - full response:', data);

        // Check if there are validation errors with details
        if (data.validationErrors || data.errors) {
          const errors = data.validationErrors || data.errors;

          // Extract all error messages from the validation errors object
          Object.keys(errors).forEach(key => {
            const errorArray = errors[key];
            if (Array.isArray(errorArray)) {
              errorMessages.push(...errorArray);
            } else if (typeof errorArray === 'string') {
              errorMessages.push(errorArray);
            }
          });
        }

        // Check for Message property (capital M)
        if (data.Message) {
          errorMessages.push(data.Message);
        }

        // If no specific errors found, use the generic message
        if (errorMessages.length === 0) {
          errorMessages.push(data.message || data.title || 'Buchung fehlgeschlagen');
        }

        // Remove duplicates and set validation errors
        const uniqueErrors = Array.from(new Set(errorMessages));
        setValidationErrors(uniqueErrors);

        // Scroll to top of modal to show errors
        const modalContent = document.querySelector('.improved-modal-content');
        if (modalContent) {
          modalContent.scrollTop = 0;
        }
      }
    } catch (error) {
      console.error('Booking error:', error);
      alert('Ein Fehler ist aufgetreten');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="improved-modal-overlay" onClick={onClose}>
      <div className="improved-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="improved-modal-header">
          <div>
            <h2>Buchung erstellen</h2>
            <p className="modal-date">{formatDate(selectedSlot.date)}</p>
          </div>
          <button onClick={onClose} className="improved-modal-close">
            <X size={24} />
          </button>
        </div>

        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <div className="validation-errors-container" style={{
            margin: '20px',
            padding: '16px',
            backgroundColor: '#FEE2E2',
            border: '2px solid #EF4444',
            borderRadius: '8px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div style={{
                fontSize: '24px',
                color: '#DC2626',
                flexShrink: 0
              }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#991B1B'
                }}>
                  Buchung nicht möglich
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {validationErrors.map((error, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#FEF2F2',
                      borderLeft: '3px solid #DC2626',
                      fontSize: '14px',
                      color: '#7F1D1D',
                      lineHeight: '1.5'
                    }}>
                      {error}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setValidationErrors([])}
                  style={{
                    marginTop: '12px',
                    padding: '6px 12px',
                    fontSize: '13px',
                    color: '#DC2626',
                    backgroundColor: 'transparent',
                    border: '1px solid #DC2626',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Verstanden
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="improved-modal-body">
          {/* Variant Selection Dropdown */}
          <div className="modal-section">
            <h3>Floßfahrt-Variante wählen</h3>
            <select
              className="variant-dropdown"
              value={selectedSlot.id}
              onChange={(e) => handleVariantChange(parseInt(e.target.value))}
            >
              {availableSlotsForDay
                .sort((a, b) => a.time.localeCompare(b.time))
                .map((slotOption) => (
                  <option key={slotOption.id} value={slotOption.id}>
                    {formatTime(slotOption.time)} Uhr - {getTourTypeName(slotOption.tourType)}
                  </option>
                ))}
            </select>
          </div>

          {/* Tour Information */}
          <div className="modal-section">
            <h3>Tour-Details</h3>
            <div className="tour-info-card">
              <div className="tour-info-row">
                <span className="label">Tour-Typ:</span>
                <span className="value">{getTourTypeName(selectedSlot.tourType)}</span>
              </div>
              <div className="tour-info-row">
                <span className="label">Uhrzeit:</span>
                <span className="value">{formatTime(selectedSlot.time)} Uhr</span>
              </div>
              <div className="tour-info-row">
                <span className="label">{pricingMode === 'FixedPrice' ? 'Festpreis:' : 'Preis pro Person:'}</span>
                <span className="value">
                  {loadingPrice ? (
                    <span className="loading-price">Berechne...</span>
                  ) : (
                    <>
                      {formatCurrency(currentPricePerPerson)}
                      {pricingMode !== 'FixedPrice' && tierDescription && tierDescription !== 'Basispreis' && (
                        <span className="tier-badge" style={{ marginLeft: '8px', fontSize: '0.85em', color: '#10b981' }}>
                          ({tierDescription})
                        </span>
                      )}
                    </>
                  )}
                </span>
              </div>
              <div className="tour-info-row">
                <span className="label">Verfügbare Plätze:</span>
                <span className="value">{selectedSlot.availableCapacity} von {selectedSlot.maxCapacity}</span>
              </div>
            </div>
          </div>

          {/* Participants Selection */}
          <div className="modal-section">
            <h3>Anzahl Teilnehmer</h3>
            <div className="participants-control">
              <button
                onClick={() => setParticipants(Math.max(effectiveMinParticipants, participants - 1))}
                className="participant-btn"
                disabled={participants <= effectiveMinParticipants}
              >
                −
              </button>
              <input
                type="number"
                value={participants}
                onChange={(e) => {
                  // Allow free typing - only store the raw value
                  const value = parseInt(e.target.value);
                  if (!isNaN(value)) {
                    setParticipants(value);
                  } else if (e.target.value === '') {
                    // Allow empty field during typing
                    setParticipants(0);
                  }
                }}
                onBlur={(e) => {
                  // Validate only when leaving the field
                  const value = parseInt(e.target.value);
                  if (isNaN(value) || value < effectiveMinParticipants) {
                    setParticipants(effectiveMinParticipants);
                  } else if (value > selectedSlot.availableCapacity) {
                    setParticipants(selectedSlot.availableCapacity);
                  }
                }}
                className="participant-input"
                min={effectiveMinParticipants}
                max={selectedSlot.availableCapacity}
              />
              <button
                onClick={() => setParticipants(Math.min(selectedSlot.availableCapacity, participants + 1))}
                className="participant-btn"
                disabled={participants >= selectedSlot.availableCapacity}
              >
                +
              </button>
              <span className="participant-label">Personen</span>
            </div>
            <p className="participant-hint">
              Minimum: {effectiveMinParticipants}{effectiveMinParticipants === 1 ? ' (Termin bestätigt, Kleingruppen und Einzelpersonen buchbar)' : ''} | Maximum: {selectedSlot.availableCapacity} Personen
            </p>
          </div>

          {/* Addons Section */}
          <div className="modal-section">
            <h3>Leistungen zu {getTourTypeName(selectedSlot.tourType)}</h3>
            {loadingAddons ? (
              <div className="addon-loading">
                <div className="spinner-small"></div>
              </div>
            ) : addons.length > 0 ? (
              <>
                {/* Bereich 1: Auswahl pro Teilnehmer (isOptionalChoice === true) */}
                {optionalChoiceAddons.length > 0 && (
                  <div style={{
                    background: '#f0fdf4',
                    border: '2px solid #16a34a',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>🎯</span>
                      <h4 style={{ margin: 0, color: '#15803d', fontSize: '1.1rem' }}>Auswahl pro Teilnehmer</h4>
                    </div>
                    <p style={{ color: '#166534', marginBottom: '1rem', fontSize: '0.9rem' }}>
                      {getOptionalChoiceMultiplier() > 1
                        ? `Bitte wählen Sie für jeden Teilnehmer ${getOptionalChoiceMultiplier()} Optionen. Die Summe muss ${getOptionalChoiceExpectedTotal()} ergeben.`
                        : `Bitte wählen Sie für jeden Teilnehmer eine Option. Die Summe muss ${participants} ergeben.`
                      }
                    </p>

                    <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
                      {optionalChoiceAddons.map((addon) => {
                        const maxForThis = getMaxForOptionalAddon(addon);
                        const optionalTotal = getOptionalChoiceTotal();

                        return (
                          <div
                            key={addon.addOnServiceId}
                            style={{
                              background: 'white',
                              border: '1px solid #86efac',
                              borderRadius: '8px',
                              padding: '1rem'
                            }}
                          >
                            <h5 style={{ margin: '0 0 0.5rem 0', color: '#1a2332', fontSize: '0.95rem' }}>
                              {addon.addOnServiceName}
                            </h5>
                            {/* Keine Preisanzeige in Auswahlliste für Kunden */}

                            {/* Quantity Controls */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <button
                                onClick={() => handleOptionalChoiceQuantityChange(addon.addOnServiceId, -1)}
                                disabled={addon.quantityTotal === 0}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px',
                                  background: addon.quantityTotal === 0 ? '#f0f0f0' : '#dcfce7',
                                  cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                                  opacity: addon.quantityTotal === 0 ? 0.5 : 1,
                                  fontSize: '1rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                value={addon.quantityTotal}
                                onChange={(e) => handleOptionalChoiceInputChange(addon.addOnServiceId, e.target.value)}
                                min="0"
                                max={maxForThis}
                                style={{
                                  width: '4rem',
                                  textAlign: 'center',
                                  fontWeight: 'bold',
                                  fontSize: '1.1rem',
                                  padding: '0.4rem',
                                  border: '2px solid #16a34a',
                                  borderRadius: '6px'
                                }}
                              />
                              <button
                                onClick={() => handleOptionalChoiceQuantityChange(addon.addOnServiceId, 1)}
                                disabled={optionalTotal >= getOptionalChoiceExpectedTotal()}
                                style={{
                                  padding: '0.4rem 0.6rem',
                                  border: '1px solid #16a34a',
                                  borderRadius: '4px',
                                  background: optionalTotal >= getOptionalChoiceExpectedTotal() ? '#f0f0f0' : '#16a34a',
                                  color: optionalTotal >= getOptionalChoiceExpectedTotal() ? '#999' : 'white',
                                  cursor: optionalTotal >= getOptionalChoiceExpectedTotal() ? 'not-allowed' : 'pointer',
                                  fontSize: '1rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                +
                              </button>
                            </div>

                            {/* Status Badge */}
                            {addon.quantityTotal > 0 && addon.quantityPaid === 0 && (
                              <div style={{
                                marginTop: '0.5rem',
                                background: '#dcfce7',
                                border: '1px solid #16a34a',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                color: '#15803d',
                                textAlign: 'center',
                                fontWeight: 'bold'
                              }}>
                                inklusive
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Validation Status */}
                    <div style={{
                      marginTop: '1rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: isOptionalChoiceValid() ? '#dcfce7' : '#fef9c3',
                      border: isOptionalChoiceValid() ? '1px solid #16a34a' : '1px solid #facc15'
                    }}>
                      {isOptionalChoiceValid() ? (
                        <>
                          <span style={{ fontSize: '1.2rem' }}>✓</span>
                          <span style={{ color: '#15803d', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            {getOptionalChoiceMultiplier() > 1
                              ? `Alle ${participants} Teilnehmer haben ${getOptionalChoiceMultiplier()} Auswahlen (${getOptionalChoiceTotal()}/${getOptionalChoiceExpectedTotal()})`
                              : `Alle ${participants} Teilnehmer haben eine Auswahl (${getOptionalChoiceTotal()}/${getOptionalChoiceExpectedTotal()})`
                            }
                          </span>
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                          <span style={{ color: '#854d0e', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            Noch {getOptionalChoiceExpectedTotal() - getOptionalChoiceTotal()} Auswahl(en) offen ({getOptionalChoiceTotal()}/{getOptionalChoiceExpectedTotal()})
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Bereich 2: Inklusivleistungen (quantityPaid === 0 && quantityTotal > 0) */}
                {inclusiveAddons.length > 0 && (
                  <div className="addons-list" style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem 0' }}>
                      <span style={{ fontSize: '1.25rem' }}>✅</span>
                      <h4 style={{ margin: 0, color: '#16a34a' }}>Inklusivleistungen</h4>
                    </div>
                    {inclusiveAddons.map((addon) => {
                      const index = addons.findIndex(a => a.addOnServiceId === addon.addOnServiceId);
                      return (
                        <div key={addon.addOnServiceId} className="addon-item" style={{ borderColor: '#86efac', background: '#f0fdf4' }}>
                          <div className="addon-header">
                            <div className="addon-name">{addon.addOnServiceName}</div>
                            <span style={{ color: '#16a34a', fontWeight: 'bold', fontSize: '0.9rem' }}>inklusive</span>
                          </div>

                          <div className="addon-controls">
                            <button
                              onClick={() => handleAddonQuantityChange(index, addon.quantityTotal - 1)}
                              className="addon-btn"
                              disabled={addon.quantityTotal <= 0}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              value={addon.quantityTotal}
                              onChange={(e) => handleAddonQuantityChange(index, parseInt(e.target.value) || 0)}
                              className="addon-input"
                              min={0}
                            />
                            <button
                              onClick={() => handleAddonQuantityChange(index, addon.quantityTotal + 1)}
                              className="addon-btn"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bereich 3: Extras (quantityPaid > 0 oder quantityTotal === 0) */}
                {extraAddons.length > 0 && (
                  <div className="addons-list">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 1rem 0' }}>
                      <span style={{ fontSize: '1.25rem' }}>➕</span>
                      <h4 style={{ margin: 0, color: '#dc2626' }}>Zusätzliche Speisen</h4>
                    </div>
                    {extraAddons.map((addon) => {
                      const index = addons.findIndex(a => a.addOnServiceId === addon.addOnServiceId);
                      // Bruttopreis berechnen mit individuellem Steuersatz
                      const taxRate = addon.taxRate ?? 0.19;
                      const grossPricePerUnit = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + taxRate);
                      const grossSubtotal = addon.quantityPaid * grossPricePerUnit;

                      return (
                        <div key={addon.addOnServiceId} className="addon-item" style={{ borderColor: '#fecaca', background: addon.quantityPaid > 0 ? '#fef2f2' : 'white' }}>
                          <div className="addon-header">
                            <div className="addon-name">{addon.addOnServiceName}</div>
                            <div style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '0.9rem' }}>
                              {formatCurrency(grossPricePerUnit).replace(' €', '')}/Stk.
                            </div>
                          </div>

                          <div className="addon-controls">
                            <button
                              onClick={() => handleAddonQuantityChange(index, addon.quantityTotal - 1)}
                              className="addon-btn"
                              disabled={addon.quantityTotal <= 0}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              value={addon.quantityTotal}
                              onChange={(e) => handleAddonQuantityChange(index, parseInt(e.target.value) || 0)}
                              className="addon-input"
                              min={0}
                            />
                            <button
                              onClick={() => handleAddonQuantityChange(index, addon.quantityTotal + 1)}
                              className="addon-btn"
                            >
                              +
                            </button>
                          </div>

                          {(addon.quantityFree > 0 || addon.quantityPaid > 0) && (
                            <div className="addon-breakdown" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                {addon.quantityFree > 0 && (
                                  <span style={{
                                    color: '#16a34a',
                                    fontWeight: 'bold',
                                    background: '#dcfce7',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.85rem'
                                  }}>
                                    {addon.quantityFree}× inklusive
                                  </span>
                                )}
                                {addon.quantityPaid > 0 && (
                                  <span className="addon-paid" style={{ color: '#dc2626' }}>
                                    {addon.quantityPaid}× kostenpflichtig
                                  </span>
                                )}
                              </div>
                              {addon.quantityPaid > 0 && (
                                <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                                  = {formatCurrency(grossSubtotal)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bereich 4: Übernachtung/Zimmerbelegung (isAccommodation === true) */}
                {accommodationAddons.length > 0 && (
                  <div style={{
                    background: '#f0f9ff',
                    border: '2px solid #0284c7',
                    borderRadius: '12px',
                    padding: '1.25rem',
                    marginTop: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>🏨</span>
                        <h4 style={{ margin: 0, color: '#0369a1', fontSize: '1.1rem' }}>Übernachtung / Zimmerbelegung</h4>
                      </div>
                      <button
                        onClick={resetAccommodationToOptimal}
                        style={{
                          padding: '0.4rem 0.75rem',
                          fontSize: '0.8rem',
                          background: '#0284c7',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Optimal verteilen
                      </button>
                    </div>

                    {/* Bed Counter */}
                    <div style={{
                      background: getAccommodationBedStatus() === 'ok' ? '#dcfce7' :
                                  getAccommodationBedStatus() === 'warning' ? '#fef9c3' : '#fee2e2',
                      border: `1px solid ${getAccommodationBedStatus() === 'ok' ? '#16a34a' :
                                           getAccommodationBedStatus() === 'warning' ? '#facc15' : '#ef4444'}`,
                      borderRadius: '8px',
                      padding: '0.75rem 1rem',
                      marginBottom: '1rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.5rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.2rem' }}>
                          {getAccommodationBedStatus() === 'ok' ? '✓' :
                           getAccommodationBedStatus() === 'warning' ? '⚠️' : '❌'}
                        </span>
                        <span style={{
                          color: getAccommodationBedStatus() === 'ok' ? '#15803d' :
                                 getAccommodationBedStatus() === 'warning' ? '#854d0e' : '#991b1b',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}>
                          Betten: {getAccommodationTotalBeds()} / {participants} Teilnehmer
                          {getAccommodationBedStatus() === 'error' && ' (Zu wenig Betten)'}
                        </span>
                      </div>
                      {/* Show extra cost for excess beds */}
                      {getAccommodationBedStatus() === 'warning' && (() => {
                        const extraCost = accommodationAddons.reduce((sum, a) => sum + a.subtotal, 0);
                        if (extraCost > 0) {
                          const extraCostGross = accommodationAddons.reduce((sum, a) => {
                            const taxRate = a.taxRate ?? 0.19;
                            return sum + (a.quantityPaid * (a.grossPricePerUnit || a.pricePerUnit * (1 + taxRate)));
                          }, 0);
                          return (
                            <span style={{ color: '#dc2626', fontWeight: 'bold', fontSize: '0.9rem' }}>
                              +{formatCurrency(extraCostGross)} extra
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    {/* Room List */}
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {accommodationAddons.map((addon) => (
                        <div
                          key={addon.addOnServiceId}
                          style={{
                            background: 'white',
                            border: '1px solid #7dd3fc',
                            borderRadius: '8px',
                            padding: '1rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <h5 style={{ margin: '0 0 0.25rem 0', color: '#1a2332', fontSize: '0.95rem' }}>
                              {addon.addOnServiceName}
                            </h5>
                            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                              {addon.bedsPerUnit} {addon.bedsPerUnit === 1 ? 'Bett' : 'Betten'} pro Zimmer
                            </span>
                          </div>

                          {/* Quantity Controls - Badges links, dann Steuerelemente */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {/* Badges links (feste Breite für stabiles Layout) */}
                            <div style={{ display: 'flex', gap: '0.25rem', minWidth: '120px', justifyContent: 'flex-end' }}>
                              {addon.quantityTotal > 0 && addon.quantityFree > 0 && (
                                <span style={{
                                  background: '#dcfce7',
                                  border: '1px solid #16a34a',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#15803d',
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {addon.quantityFree}× inkl.
                                </span>
                              )}
                              {addon.quantityTotal > 0 && addon.quantityPaid > 0 && (
                                <span style={{
                                  background: '#fee2e2',
                                  border: '1px solid #ef4444',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  color: '#dc2626',
                                  fontWeight: 'bold',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {addon.quantityPaid}× extra
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleAccommodationQuantityChange(addon.addOnServiceId, -1)}
                              disabled={addon.quantityTotal === 0}
                              style={{
                                padding: '0.4rem 0.6rem',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                background: addon.quantityTotal === 0 ? '#f0f0f0' : '#e0f2fe',
                                cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                                opacity: addon.quantityTotal === 0 ? 0.5 : 1,
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }}
                            >
                              −
                            </button>
                            <input
                              type="number"
                              value={addon.quantityTotal}
                              onChange={(e) => {
                                const newVal = parseInt(e.target.value) || 0;
                                const delta = newVal - addon.quantityTotal;
                                handleAccommodationQuantityChange(addon.addOnServiceId, delta);
                              }}
                              min="0"
                              style={{
                                width: '4rem',
                                textAlign: 'center',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                padding: '0.4rem',
                                border: '2px solid #0284c7',
                                borderRadius: '6px'
                              }}
                            />
                            <button
                              onClick={() => handleAccommodationQuantityChange(addon.addOnServiceId, 1)}
                              style={{
                                padding: '0.4rem 0.6rem',
                                border: '1px solid #0284c7',
                                borderRadius: '4px',
                                background: '#0284c7',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                fontWeight: 'bold'
                              }}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-addons">
                <p>Für diese Tour-Variante sind keine Zusatzoptionen konfiguriert.</p>
              </div>
            )}

            {/* Add Manual Addon Button */}
            {/* T-058 Stufe 2 (v1.8.61): nur, wenn noch etwas aus dem Online-Angebot fehlt (optionale Speisen stehen schon oben mit Menge 0) */}
            {getUnselectedAddons().length > 0 && (
            <div className="addon-add-section">
              {!showAddonSelector ? (
                <button
                  className="btn-add-addon"
                  onClick={() => setShowAddonSelector(true)}
                  disabled={loadingAvailableAddons}
                >
                  + Weitere Optionen hinzufügen
                </button>
              ) : (
                <div className="addon-selector">
                  <div className="addon-selector-header">
                    <h4>Zusatzoption auswählen</h4>
                    <button
                      className="addon-selector-close"
                      onClick={() => setShowAddonSelector(false)}
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="addon-selector-list">
                    {getUnselectedAddons().length > 0 ? (
                      getUnselectedAddons().map(addon => (
                        <div
                          key={addon.id}
                          className="addon-selector-item"
                          onClick={() => handleAddManualAddon(addon.id)}
                        >
                          <div className="addon-selector-name">{addon.name}</div>
                          {/* Keine Preisanzeige in Auswahlliste für Kunden */}
                        </div>
                      ))
                    ) : (
                      <div className="addon-selector-empty">
                        Alle verfügbaren Zusatzoptionen wurden bereits hinzugefügt.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Kontaktdaten, Pflichtfelder (Anlage v1.4 Abschnitt 2.8 Punkte 2 und 3) */}
          <div className="modal-section">
            <h3>Ihre Kontaktdaten</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem' }}>
              {/* T-099 (v1.8.24): Kundentyp wählbar, Namensfeld bei Geschäftskunde, Verein, Verwaltung (Kundin 23.09.2026) */}
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Sie buchen als *
                <select value={contact.customerType} onChange={(e) => updateContact('customerType', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                  {CUSTOMER_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              {hasOrganizationName(contact.customerType) && (
                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                  {organizationNameLabel(contact.customerType)} *
                  <input type="text" value={contact.companyName} onChange={(e) => updateContact('companyName', e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                </label>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Anrede *
                <select value={contact.salutation} onChange={(e) => updateContact('salutation', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                  <option value="">Bitte wählen</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Vorname *
                <input type="text" value={contact.firstName} onChange={(e) => updateContact('firstName', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Nachname *
                <input type="text" value={contact.lastName} onChange={(e) => updateContact('lastName', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Straße *
                <input type="text" value={contact.street} onChange={(e) => updateContact('street', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Hausnummer *
                <input type="text" value={contact.houseNumber} onChange={(e) => updateContact('houseNumber', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                PLZ *
                <input type="text" inputMode="numeric" value={contact.postalCode} onChange={(e) => updateContact('postalCode', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Ort *
                <input type="text" value={contact.city} onChange={(e) => updateContact('city', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Handynummer *
                <input type="tel" value={contact.phone} onChange={(e) => updateContact('phone', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              {/* T-100 (v1.8.24): Festnetz freiwillig (Kundin 23.09.2026) */}
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                Festnetz (optional)
                <input type="tel" value={contact.landlinePhone} onChange={(e) => updateContact('landlinePhone', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                E-Mail-Adresse *
                <input type="email" value={contact.email} onChange={(e) => updateContact('email', e.target.value)}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem' }}>
                E-Mail-Adresse wiederholen *
                <input type="email" value={contact.emailRepeat} onChange={(e) => updateContact('emailRepeat', e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px' }} />
              </label>
            </div>
            {/* T-073 (v1.8.16): Notiz an Frankenfloß, optional, Anlage 2.1 Punkt 11 */}
            <label style={{ display: 'flex', flexDirection: 'column', fontSize: '0.9rem', gap: '0.25rem', marginTop: '0.75rem' }}>
              Notiz an Frankenfloß (optional)
              <textarea
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value.substring(0, SPECIAL_REQUESTS_MAX))}
                maxLength={SPECIAL_REQUESTS_MAX}
                rows={3}
                placeholder="z. B. Rollstuhlfahrer, Geburtstag, Ankunftszeit"
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontFamily: 'inherit', resize: 'vertical' }}
              />
              <span style={{ fontSize: '0.8rem', color: '#666', alignSelf: 'flex-end' }}>
                {specialRequests.length} / {SPECIAL_REQUESTS_MAX} Zeichen
              </span>
            </label>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
              Hinweis: Eine abweichende Rechnungsadresse geben Sie bitte bereits hier im Buchungsvorgang korrekt an.
            </p>
          </div>

          {/* Price Summary - Bruttopreise */}
          <div className="modal-section">
            <h3>Preisübersicht</h3>
            <div className="price-card">
              <div className="price-row">
                <span>
                  {pricingMode === 'FixedPrice' && fixedPrice ? (
                    <>Floßfahrt Festpreis (für {participants} Personen)</>
                  ) : (
                    <>
                      Floßfahrt ({participants} Personen × {formatCurrency(currentPricePerPerson)})
                      {tierDescription && tierDescription !== 'Basispreis' && (
                        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
                          Staffelpreis: {tierDescription}
                        </div>
                      )}
                    </>
                  )}
                </span>
                <span>{formatCurrency(pricingMode === 'FixedPrice' && fixedPrice ? fixedPrice : currentPricePerPerson * participants)}</span>
              </div>
              {addons.filter(a => a.subtotal > 0).map((addon, idx) => {
                const grossPrice = addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19));
                const grossSubtotal = addon.quantityPaid * grossPrice;
                return (
                  <div key={idx} className="price-row addon-price">
                    <span>{addon.addOnServiceName} ({addon.quantityPaid}×)</span>
                    <span>{formatCurrency(grossSubtotal)}</span>
                  </div>
                );
              })}

              {/* Zwischensumme und Gutschein-Rabatt */}
              {appliedVoucher && (
                <>
                  <div className="price-divider"></div>
                  <div className="price-row">
                    <span>Zwischensumme</span>
                    <span>{formatCurrency(calculatePriceBreakdown().subtotal)}</span>
                  </div>
                  <div className="price-row" style={{ color: '#16a34a' }}>
                    <span>Gutschein ({appliedVoucher.code})</span>
                    <span style={{ fontWeight: 'bold' }}>-{formatCurrency(calculatePriceBreakdown().voucherDiscount)}</span>
                  </div>
                </>
              )}

              <div className="price-divider"></div>

              <div className="price-row total">
                <span>Gesamtpreis</span>
                <span className="highlight">{formatCurrency(calculatePriceBreakdown().brutto)}</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: '#666', textAlign: 'right', margin: '0.25rem 0 0.5rem 0' }}>inkl. MwSt.</p>
              <div className="price-row deposit">
                <span>Anzahlung ({Math.round(depositRate * 100)}%)</span>
                <span>{formatCurrency(calculatePriceBreakdown().brutto * depositRate)}</span>
              </div>

              {/* Komplettzahlungs-Wahl (Anlage v1.3 Abschnitt 2.5 Punkt 3) */}
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                  <input
                    type="radio"
                    name="paymentChoice"
                    checked={paymentChoice === 'deposit'}
                    onChange={() => setPaymentChoice('deposit')}
                  />
                  <span>
                    Anzahlung jetzt ({Math.round(depositRate * 100)}%,{' '}
                    {formatCurrency(calculatePriceBreakdown().brutto * depositRate)}), Restzahlung 25 Tage vor der Fahrt
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="paymentChoice"
                    checked={paymentChoice === 'full'}
                    onChange={() => setPaymentChoice('full')}
                  />
                  <span>
                    Gesamtbetrag sofort zahlen ({formatCurrency(calculatePriceBreakdown().brutto)})
                  </span>
                </label>
              </div>
            </div>

            {/* Voucher Code Input - Collapsible */}
            <div style={{
              marginTop: '1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <button
                type="button"
                onClick={() => setVoucherExpanded(!voucherExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem 1rem',
                  background: '#f9fafb',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>🎁</span>
                  <span style={{ fontWeight: '500' }}>
                    {appliedVoucher ? `Gutschein angewendet: ${appliedVoucher.code}` : 'Gutschein einlösen'}
                  </span>
                  {appliedVoucher && (
                    <span style={{ color: '#16a34a', fontWeight: 'bold' }}>
                      (-{formatCurrency(appliedVoucher.amountToApply)})
                    </span>
                  )}
                </div>
                {voucherExpanded ? (
                  <ChevronUp style={{ width: '1.25rem', height: '1.25rem', color: '#6b7280' }} />
                ) : (
                  <ChevronDown style={{ width: '1.25rem', height: '1.25rem', color: '#6b7280' }} />
                )}
              </button>

              {voucherExpanded && (
                <div style={{ padding: '1rem', borderTop: '1px solid #e5e7eb' }}>
                  <VoucherCodeInput
                    bookingTotal={calculateSubtotal()}
                    onVoucherApplied={(voucher) => setAppliedVoucher(voucher)}
                    onVoucherRemoved={() => setAppliedVoucher(null)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="improved-modal-footer">
          <button onClick={onClose} className="btn-cancel">
            Abbrechen
          </button>
          <button
            onClick={handleSubmit}
            className="btn-submit"
            disabled={loading || participants < effectiveMinParticipants || !isOptionalChoiceValid()}
            title={!isOptionalChoiceValid() ? `Bitte wählen Sie Bordspeisen für alle ${participants} Teilnehmer${getOptionalChoiceMultiplier() > 1 ? ` (${getOptionalChoiceMultiplier()}x pro Person = ${getOptionalChoiceExpectedTotal()})` : ''}` : ''}
          >
            {loading ? 'Wird gebucht...' : 'Zahlungspflichtig buchen'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImprovedBookingModal;
